import { Link } from 'react-router-dom'
import {
  User,
  Heart,
  FileText,
  Search,
  Calculator,
  RefreshCw,
  Phone,
  Mail,
  ChevronRight,
} from 'lucide-react'
import { Badge, Button, Card, CardContent, StatusBadge } from '@/components/ui'
import { useFavoritesStore } from '@/stores/favorites'
import { getVehicleById } from '@/data/vehicles'
import { formatPrice } from '@/utils/format'

// Mock user data (in real app this would come from auth context)
const mockUser = {
  firstName: 'John',
  lastName: 'Doe',
  email: 'john.doe@example.com',
  phone: '(555) 123-4567',
  address: {
    street: '123 Main Street',
    city: 'Austin',
    state: 'TX',
    zipCode: '78701',
  },
}

// Mock applications
const mockApplications: Array<{
  id: string
  status: 'pending' | 'approved' | 'under_review'
  createdAt: string
}> = []

export function AccountPage() {
  const { favorites } = useFavoritesStore()
  const savedVehicles = favorites.map((id) => getVehicleById(id)).filter(Boolean)

  const quickActions = [
    { icon: Search, title: 'Browse Vehicles', href: '/vehicles' },
    { icon: Calculator, title: 'Payment Calculator', href: '/financing' },
    { icon: RefreshCw, title: 'Trade-In Estimator', href: '/trade-in' },
    { icon: FileText, title: 'Apply for Financing', href: '/apply' },
  ]

  return (
    <div className="bg-gray-50 min-h-screen">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">My Account</h1>
          <p className="mt-2 text-gray-600">
            Welcome back, {mockUser.firstName}!
          </p>
        </div>

        <div className="grid gap-8 lg:grid-cols-3">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* Saved Vehicles */}
            <Card>
              <CardContent>
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                    <Heart className="h-5 w-5 text-red-500" />
                    Saved Vehicles
                  </h2>
                  <Link
                    to="/favorites"
                    className="text-sm text-primary-600 hover:underline"
                  >
                    View All
                  </Link>
                </div>

                {savedVehicles.length === 0 ? (
                  <div className="text-center py-8">
                    <Heart className="mx-auto h-12 w-12 text-gray-300" />
                    <p className="mt-4 text-gray-600">No saved vehicles yet</p>
                    <Link to="/vehicles" className="inline-block mt-4">
                      <Button variant="outline">Browse Vehicles</Button>
                    </Link>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {savedVehicles.slice(0, 3).map((vehicle) => {
                      if (!vehicle) return null
                      const title = `${vehicle.year} ${vehicle.make} ${vehicle.model}`
                      return (
                        <Link
                          key={vehicle.id}
                          to={`/vehicles/${vehicle.id}`}
                          className="flex items-center gap-4 rounded-lg border border-gray-200 p-4 transition-colors hover:border-primary-300 hover:bg-primary-50/50"
                        >
                          <div className="h-16 w-24 flex-shrink-0 overflow-hidden rounded-md bg-gray-100">
                            {vehicle.photos[0] && (
                              <img
                                src={vehicle.photos[0]}
                                alt={title}
                                className="h-full w-full object-cover"
                              />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <h3 className="font-semibold text-gray-900 truncate">
                              {title} {vehicle.trim}
                            </h3>
                            <p className="text-lg font-bold text-gray-900">
                              {formatPrice(vehicle.priceCents)}
                            </p>
                            <StatusBadge status={vehicle.status} size="sm" />
                          </div>
                          <ChevronRight className="h-5 w-5 text-gray-400 flex-shrink-0" />
                        </Link>
                      )
                    })}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Applications */}
            <Card>
              <CardContent>
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                    <FileText className="h-5 w-5 text-primary-600" />
                    My Applications
                  </h2>
                  <Link
                    to="/apply"
                    className="text-sm text-primary-600 hover:underline"
                  >
                    Start New Application
                  </Link>
                </div>

                {mockApplications.length === 0 ? (
                  <div className="text-center py-8">
                    <FileText className="mx-auto h-12 w-12 text-gray-300" />
                    <p className="mt-4 text-gray-600">No applications yet</p>
                    <Link to="/apply" className="inline-block mt-4">
                      <Button variant="outline">Apply for Financing</Button>
                    </Link>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {mockApplications.map((app) => (
                      <div
                        key={app.id}
                        className="rounded-lg border border-gray-200 p-4"
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-medium text-gray-900">
                              Application #{app.id}
                            </p>
                            <p className="text-sm text-gray-500">
                              Submitted: {app.createdAt}
                            </p>
                          </div>
                          <Badge variant="default" className="capitalize">
                            {app.status.replace(/_/g, ' ')}
                          </Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Profile Card */}
            <Card>
              <CardContent>
                <div className="flex items-center gap-3 mb-4">
                  <div className="h-12 w-12 rounded-full bg-primary-100 flex items-center justify-center">
                    <User className="h-6 w-6 text-primary-600" />
                  </div>
                  <div>
                    <h2 className="font-semibold text-gray-900">Profile</h2>
                  </div>
                </div>
                <dl className="space-y-3 text-sm">
                  <div>
                    <dt className="text-gray-500">Name</dt>
                    <dd className="font-medium text-gray-900">
                      {mockUser.firstName} {mockUser.lastName}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-gray-500">Email</dt>
                    <dd className="font-medium text-gray-900">{mockUser.email}</dd>
                  </div>
                  <div>
                    <dt className="text-gray-500">Phone</dt>
                    <dd className="font-medium text-gray-900">{mockUser.phone}</dd>
                  </div>
                  <div>
                    <dt className="text-gray-500">Address</dt>
                    <dd className="font-medium text-gray-900">
                      {mockUser.address.street}
                      <br />
                      {mockUser.address.city}, {mockUser.address.state}{' '}
                      {mockUser.address.zipCode}
                    </dd>
                  </div>
                </dl>
                <Button variant="outline" className="mt-6 w-full" size="sm">
                  Edit Profile
                </Button>
              </CardContent>
            </Card>

            {/* Quick Actions */}
            <Card>
              <CardContent>
                <h2 className="font-semibold text-gray-900 mb-4">Quick Actions</h2>
                <div className="space-y-2">
                  {quickActions.map((action) => (
                    <Link
                      key={action.title}
                      to={action.href}
                      className="flex items-center gap-3 rounded-lg p-3 text-sm transition-colors hover:bg-gray-50"
                    >
                      <action.icon className="h-5 w-5 text-gray-500" />
                      <span className="text-gray-700">{action.title}</span>
                    </Link>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Help */}
            <Card className="bg-primary-50 border-primary-200">
              <CardContent>
                <h3 className="font-semibold text-gray-900">Need Help?</h3>
                <p className="mt-2 text-sm text-gray-600">
                  Our team is here to assist you with any questions.
                </p>
                <div className="mt-4 space-y-2 text-sm">
                  <a
                    href="tel:+15551234567"
                    className="flex items-center gap-2 text-primary-600 hover:underline"
                  >
                    <Phone className="h-4 w-4" />
                    (555) 123-4567
                  </a>
                  <a
                    href="mailto:support@bespokian-auto.com"
                    className="flex items-center gap-2 text-primary-600 hover:underline"
                  >
                    <Mail className="h-4 w-4" />
                    support@bespokian-auto.com
                  </a>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  )
}
